import "@/css/components/tooltip.scss";
import { createRequiredContext } from "@enymo/react-better-context";
import useScreenSize from "@enymo/react-screen-size-hook";
import { isNotNull } from "@enymo/ts-nullsafe";
import classNames from "classnames";
import React, { useMemo, useRef } from "react";
import { useHovering } from "../Hooks/HoveringHook";

export interface TooltipProps {
    children?: React.ReactNode,
    padding?: string,
    preferredPosition?: "top" | "bottom",
}

const [Provider, useTooltipParent] = createRequiredContext<{
    show: boolean,
    parentRect?: DOMRect,
}>("The Tooltip component must be wrapped in a TooltipParent component.");

export default function Tooltip({
    children,
    padding,
    preferredPosition = "top",
}: TooltipProps) {
    const ref = useRef<HTMLDivElement>(null);
    const { show, parentRect } = useTooltipParent();
    const { width: screenWidth, height: screenHeight } = useScreenSize();

    const styles = useMemo<React.CSSProperties[] | undefined>(() => {
        if (isNotNull(ref.current) && isNotNull(parentRect)) {
            const { width, height } = ref.current.getBoundingClientRect();
            const padding = 10;

            let showPosition = preferredPosition;
            if (showPosition === "top" && parentRect.y - height - 3 < padding) {
                showPosition = "bottom";
            }
            else if (showPosition === "bottom" && parentRect.y + parentRect.height + height + 3 > screenHeight - padding) {
                showPosition = "top";
            }

            // The width of the triangle is 11px, so we need to add 5px to the left position.
            // The width gotten from the getBoundingClientRect method is not accurate.
            if (showPosition === "top") {
                return [{
                    top: `${parentRect.y - height - 7}px`,
                    left: `${Math.min(
                        Math.max(parentRect.x + parentRect.width / 2 - width / 2, padding),
                        screenWidth - width - padding
                    )}px`,
                }, {
                    left: `${parentRect.x + parentRect.width / 2 - 5}px`,
                    top: `${parentRect.y - 7}px`,
                }]
            }
            else {
                return [{
                    top: `${parentRect.y + parentRect.height + 9}px`,
                    left: `${Math.min(
                        Math.max(parentRect.x + parentRect.width / 2 - width / 2, padding),
                        screenWidth - width - padding
                    )}px`,
                }, {
                    left: `${parentRect.x + parentRect.width / 2 - 5}px`,
                    top: `${parentRect.y + parentRect.height + 4}px`,
                    transform: "rotate(180deg)",

                }]
            }
        }
        return undefined;
    }, [ref, show, parentRect, screenWidth, screenHeight]);

    const [style, triangleStyle] = styles ?? [{}, {}];

    return (
        <div ref={ref} className={classNames("tooltip")} style={{
            ...style,
            visibility: show ? "visible" : "hidden",
        }}>
            <div className="text-container" style={{ padding }}>
                <span className="text">{children}</span>
            </div>
            <div className="triangle" style={triangleStyle} />
        </div>
    )
}

export function TooltipParent({
    className,
    children,
    disabled = false,
    style,
}: {
    className?: string,
    children: React.ReactNode,
    disabled?: boolean,
    style?: React.CSSProperties,
}) {

    const [ref, hover] = useHovering<HTMLDivElement>();
    const show = hover && !disabled;

    return (
        <div ref={ref} className={classNames(className, "tooltip-parent", { disabled })} style={style}>
            <Provider value={{ show, parentRect: ref.current?.getBoundingClientRect() }}>
                {children}
            </Provider>
        </div>
    )
}